import React from 'react';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
} from '../../../utils/seoSchemas';
import { TrendingUp, DollarSign, Globe2, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const HormuzEconomicImpact = () => {
  const title = 'Strait of Hormuz Economic Impact | Oil Price & Trade Scenarios';
  const description =
    'Scenario-based economic impact analysis of disruptions in the Strait of Hormuz, covering oil price ranges, trade disruption, shipping routes, and macroeconomic consequences with clearly caveated, data-informed estimates.';

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Economic Impact', path: '/strait-of-hormuz/economic-impact' },
    ]),
  ];

  const scenarios = [
    {
      name: 'Partial Disruption',
      severityLabel: '10–30% flow disrupted',
      icon: TrendingUp,
      oilImpact:
        'Historical episodes where a portion of flows was at risk have seen benchmark crude prices move roughly 10–25% over several weeks, depending on inventories and spare capacity.',
      tradeImpact:
        'Tankers are delayed or re-routed, but the strait remains navigable. Refiners draw on storage, and some buyers switch grades or suppliers. Freight rates typically spike, but supply chains continue operating with higher costs.',
      macroImpact:
        'Import-dependent economies face higher fuel and transport costs, adding short-term pressure to inflation. Central banks may lean more cautious, but the shock is usually absorbed without a deep global recession.',
    },
    {
      name: 'Full Blockage',
      severityLabel: '80–100% flow disrupted',
      icon: AlertTriangle,
      oilImpact:
        'A prolonged, near-complete halt could plausibly push prices tens of percent higher, for example in the order of 30–70% above pre-crisis levels, especially if it coincides with already tight markets.',
      tradeImpact:
        'Buyers scramble for alternative crude, re-routing via longer sea routes and pipelines with limited spare capacity. Refiners without alternatives may reduce runs, and some industrial users face rationing or forced curtailment.',
      macroImpact:
        'Sustained high prices and uncertainty can weaken global growth, widen current-account deficits for major importers, and trigger fiscal pressure where governments subsidise fuel prices.',
    },
    {
      name: 'Military Escalation',
      severityLabel: 'High, persistent security risk',
      icon: Globe2,
      oilImpact:
        'Even when volumes keep flowing, persistent military tension can keep a risk premium embedded in prices, with markets oscillating in wide ranges as headlines shift perceived probabilities of escalation or de‑escalation.',
      tradeImpact:
        'War-risk insurance, convoy escorts, and cautious routing decisions raise the cost of moving each barrel. Some smaller or more risk-averse operators may temporarily avoid the area, concentrating flows among a smaller number of large players.',
      macroImpact:
        'Volatility itself becomes a problem: corporates and governments face higher planning uncertainty, hedging is more expensive, and risk appetite in broader financial markets can weaken, tightening financial conditions.',
    },
  ];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/economic-impact"
        keywords="Hormuz economic impact, oil price scenarios, Hormuz disruption cost, global trade impact, shipping disruption"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-economic-impact.png"
        imageAlt="Strait of Hormuz Economic Impact - Scenario-based oil price and trade effects"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-on-surface mb-3">
          Economic Impact of Strait of Hormuz Disruptions
        </h1>
        <p className="text-slate-700 dark:text-on-surface-variant text-sm mb-2">
          Last updated: March 2026 · This page provides indicative, scenario-based analysis only and
          does not constitute investment, trading, or policy advice.
        </p>
        <p className="text-slate-600 dark:text-on-surface-variant text-base leading-relaxed">
          Roughly one fifth of global oil supply and a large share of seaborne crude exports move
          through the Strait of Hormuz. Because so much of the physical oil market is concentrated
          in a narrow corridor, disruptions here tend to have out-sized effects on prices, freight
          costs, and macroeconomic conditions. The sections below walk through how different
          disruption scenarios could influence benchmark crude prices, trade flows, and the broader
          global economy using ranges and qualitative mechanisms rather than point forecasts.
        </p>
      </header>

      {/* B. Why It Matters (Data-Driven Snapshot) */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          Why the Strait of Hormuz Matters for the Global Economy
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-4">
          Public estimates suggest that the Strait of Hormuz typically carries on the order of
          tens of millions of barrels of oil per day, which corresponds to roughly a fifth of
          global consumption and an even larger share of seaborne crude trade. When such a large
          volume is concentrated in a single chokepoint, even partial disruptions can have
          knock-on effects that extend far beyond the immediate region.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <ImpactStat
            label="Share of global oil flows"
            value="≈ 20–22%"
            description="Indicative share of worldwide oil consumption that normally passes through Hormuz in a typical year, based on public agency estimates."
          />
          <ImpactStat
            label="Daily crude & products"
            value="≈ 17–21M bbl/day"
            description="Combined crude oil and refined product volumes, including exports from key Gulf producers toward Asia, Europe, and beyond."
          />
          <ImpactStat
            label="Share of seaborne oil trade"
            value="≈ 30%"
            description="A material portion of global seaborne crude and liquids trade relies on clear passage through this single corridor."
          />
        </div>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant">
          These figures are approximate ranges compiled from widely cited public sources. They are
          intended to illustrate orders of magnitude rather than provide exact real-time values.
          Actual flows vary by year, season, and market conditions.
        </p>
      </section>

      {/* C. Scenario-Based Economic Impact */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface">
            Scenario-Based Economic Impact
          </h2>
          <Link
            to="/strait-of-hormuz/oil-impact-calculator"
            className="text-sm text-primary-600 dark:text-primary hover:underline flex items-center"
          >
            Explore custom scenarios with the Oil Impact Calculator
          </Link>
        </div>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-6">
          The figures below are indicative ranges intended to help readers reason about scale and
          direction. Actual market outcomes depend on many factors, including spare production
          capacity, inventory levels, demand conditions, and policy responses. Where possible they
          draw on patterns observed in past disruptions, but they should not be treated as precise
          forecasts.
        </p>

        <div className="space-y-6">
          {scenarios.map((scenario) => (
            <section
              key={scenario.name}
              className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-on-surface">
                    {scenario.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-on-surface-variant">
                    Approximate severity: {scenario.severityLabel}
                  </p>
                </div>
                <div className="hidden sm:flex items-center text-xs text-slate-500 dark:text-on-surface-variant">
                  <TrendingUp className="w-4 h-4 mr-1 text-primary-600" />
                  Price, trade, and macro channels combined
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4 bg-slate-100 dark:bg-surface-container-low/60">
                  <h4 className="flex items-center text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
                    <TrendingUp className="w-4 h-4 mr-2 text-primary-600" />
                    Oil price impact (illustrative)
                  </h4>
                  <p className="text-xs text-slate-700 dark:text-on-surface-variant leading-relaxed">
                    {scenario.oilImpact}
                  </p>
                </div>
                <div className="border rounded-lg p-4 bg-slate-100 dark:bg-surface-container-low/60">
                  <h4 className="flex items-center text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
                    <Globe2 className="w-4 h-4 mr-2 text-primary-600" />
                    Trade & logistics disruption
                  </h4>
                  <p className="text-xs text-slate-700 dark:text-on-surface-variant leading-relaxed">
                    {scenario.tradeImpact}
                  </p>
                </div>
                <div className="border rounded-lg p-4 bg-slate-100 dark:bg-surface-container-low/60">
                  <h4 className="flex items-center text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
                    <DollarSign className="w-4 h-4 mr-2 text-primary-600" />
                    Global macro consequences
                  </h4>
                  <p className="text-xs text-slate-700 dark:text-on-surface-variant leading-relaxed">
                    {scenario.macroImpact}
                  </p>
                </div>
              </div>
            </section>
          ))}
        </div>
      </section>

      {/* D. Channels of Transmission */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          How Disruptions Propagate Through the Global Economy
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
              Physical flows and shipping costs
            </h3>
            <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
              When transit through Hormuz is constrained, tankers may need to queue, sail at
              reduced speeds under escort, or divert to longer routes such as via the Cape of Good
              Hope. The result is more days at sea for each cargo and higher bunker fuel and crew
              costs. Freight rates and charter hire levels can move sharply in response, especially
              for specific vessel classes that are most exposed to Gulf loadings.
            </p>
            <p className="text-sm text-slate-700 dark:text-on-surface-variant">
              These higher logistics costs ultimately feed into delivered prices for refiners and
              consumers. Even if crude benchmarks remain anchored by spare capacity elsewhere,
              delivered prices into certain import regions can rise by amounts that reflect both
              freight and insurance premia, not just the value of the underlying oil.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
              Insurance, financing, and risk premia
            </h3>
            <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
              Under elevated security risk, specialised war‑risk insurance is often required for
              vessels entering designated areas. Premiums can increase several‑fold compared with
              calm periods, adding meaningful cost on a per‑voyage basis. Lenders and charterers may
              also tighten terms, which can further restrict the pool of ships willing to transit
              the region at any given time.
            </p>
            <p className="text-sm text-slate-700 dark:text-on-surface-variant">
              In financial markets, these developments are reflected in higher volatility, wider
              option implied volatilities, and shifting term structures in futures curves. Together,
              they create a risk premium that can persist even after immediate tensions ease.
            </p>
          </div>
        </div>
      </section>

      {/* E. Country & Sector Exposure */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Which Economies Are Most Exposed?
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-4">
          Countries that import a large share of their crude and refined products from the Gulf,
          especially those with limited alternative routes or strategic reserves, tend to be most
          sensitive to disruptions. Highly energy‑intensive and transport‑dependent sectors also
          feel the impact quickly through fuel costs and input prices.
        </p>
        <div className="overflow-x-auto overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container mb-4">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 dark:bg-surface-container-low">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Country / Region
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Approx. reliance on Gulf flows
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Indicative risk level
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Northeast Asia</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Often well above half</td>
                <td className="px-3 py-2 text-red-600 dark:text-red-400 font-semibold">High</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Heavy reliance on seaborne imports; pipeline alternatives exist but are limited
                  relative to total demand.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">South & Southeast Asia</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Significant but country‑specific
                </td>
                <td className="px-3 py-2 text-orange-600 dark:text-orange-400 font-semibold">
                  Medium–High
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Rapid demand growth and limited domestic production can magnify the impact of even
                  temporary disruptions.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Europe</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Mixed; more diversified via pipelines and other sea routes
                </td>
                <td className="px-3 py-2 text-yellow-600 dark:text-yellow-400 font-semibold">
                  Medium
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Less exposed to physical disruption than some Asian buyers, but still affected by
                  global price levels.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Major exporters</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Depend on open routes to reach buyers
                </td>
                <td className="px-3 py-2 text-amber-600 dark:text-amber-400 font-semibold">
                  Route‑specific
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Revenue and fiscal positions can be affected if volumes fall or discounts are
                  needed to clear cargoes.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          Exposure levels above are broad, qualitative groupings designed to complement the detailed
          country‑level view on the{' '}
          <Link
            to="/strait-of-hormuz/countries-dependent"
            className="text-primary-600 dark:text-primary underline"
          >
            Countries Dependent
          </Link>{' '}
          page.
        </p>
      </section>

      {/* F. Market Response & Policy Tools */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          How Markets and Policymakers Typically Respond
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
              Short‑term shock absorbers
            </h3>
            <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
              <li>
                <strong>Strategic reserves:</strong> Many major importers hold oil stocks
                equivalent to several months of net imports, which can be released to smooth
                temporary supply shocks.
              </li>
              <li>
                <strong>Commercial inventories:</strong> Refiners and traders can draw down
                on storage, delaying the need to adjust refinery runs immediately.
              </li>
              <li>
                <strong>Flexible refining systems:</strong> Some refineries can switch between
                different crude grades or adjust product slates in response to changing price
                signals.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-2">
              Medium‑term adjustment
            </h3>
            <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
              <li>
                <strong>Supply reallocation:</strong> Producers outside the Gulf may raise
                output where capacity and policy allow, partially offsetting lost volumes.
              </li>
              <li>
                <strong>Demand response:</strong> Higher prices encourage efficiency,
                substitution, or temporary demand restraint, especially in price‑sensitive
                segments.
              </li>
              <li>
                <strong>Policy measures:</strong> Governments may adjust fuel taxes or subsidies,
                implement temporary support for vulnerable sectors, or coordinate emergency
                responses with partners.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* G. Methodology & Disclaimer */}
      <section className="mb-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Methodology, Data Sources, and Important Disclaimers
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          This page combines publicly available information about trade volumes and historical
          disruption episodes with stylised scenario analysis. Ranges such as “10–25%” or
          “30–70%” are illustrative bands designed to capture plausible outcomes under different
          conditions, not promises or precise predictions. Actual market behaviour can be more or
          less extreme depending on how multiple factors interact at the time of any incident.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Nothing on this page should be interpreted as investment, trading, or legal advice. It is
          intended as a high-level, educational overview to help readers understand potential
          transmission channels between chokepoint risk, oil markets, shipping, and the wider
          economy. Readers should always consult multiple sources and, where appropriate,
          professional advisors before making decisions.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant">
          For users who need to explore custom numbers under specific assumptions, the{' '}
          <Link
            to="/strait-of-hormuz/oil-impact-calculator"
            className="text-primary-700 dark:text-primary-300 underline"
          >
            Oil Impact Calculator
          </Link>{' '}
          and the{' '}
          <Link
            to="/strait-of-hormuz/statistics"
            className="text-primary-700 dark:text-primary-300 underline"
          >
            Statistics
          </Link>{' '}
          page provide additional tools and reference figures.
        </p>
      </section>
    </article>
  );
};

const ImpactStat = ({ label, value, description }) => (
  <div className="rounded-stitch-xl border border-slate-200/80 bg-slate-50 p-4 dark:border-outline-variant/15 dark:bg-surface-container-low/80">
    <p className="text-xs font-medium text-slate-600 dark:text-on-surface-variant mb-1">{label}</p>
    <p className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-1">{value}</p>
    <p className="text-xs text-slate-700 dark:text-on-surface-variant leading-relaxed">{description}</p>
  </div>
);

export default HormuzEconomicImpact;

